import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Trash2, Loader2, Pause, Play, ExternalLink, ChevronDown } from 'lucide-react';
import supabase from '../../lib/supabaseClient';
import {
  card, input, btnPrimary, btnGhost, CATEGORIES, categoryPill, statusPill, PLATFORMS, relativeTime,
} from './theme';

// Build an absolute, openable href from a stored target.
// Reddit targets store just the subreddit name (e.g. "BiggerPockets"), so a bare
// href would resolve relative to the current origin. Construct the full URL.
function targetHref(t) {
  const raw = (t.url || '').trim();
  if (/^https?:\/\//i.test(raw)) return raw; // already a full URL
  if ((t.platform || '').toLowerCase() === 'reddit') {
    const sub = raw.replace(/^\/?r\//i, '').replace(/\/+$/, '');
    return `https://www.reddit.com/r/${sub}`;
  }
  return `https://${raw}`; // non-reddit bare value: assume a domain
}

const PLATFORM_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'reddit', label: 'Reddit' },
  { key: 'youtube', label: 'YouTube' },
];

/**
 * Targets — list of monitored sources.
 * Columns: platform, URL/group id, status, last scanned, total signals found.
 * Bulk pause/resume per platform + platform filter (handles 38+ targets).
 */
export default function HunterTargets() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null); // 'reddit' | 'youtube' | null
  const [showAdd, setShowAdd] = useState(false);
  const [platformFilter, setPlatformFilter] = useState('all');

  // New-target form
  const [platform, setPlatform] = useState(PLATFORMS[0]);
  const [url, setUrl] = useState('');
  const [cats, setCats] = useState([]);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('hunter_targets')
      .select('*')
      .order('created_at', { ascending: false });
    if (!error && data) setTargets(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const toggleCat = (c) =>
    setCats((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const resetForm = () => {
    setPlatform(PLATFORMS[0]);
    setUrl('');
    setCats([]);
    setShowAdd(false);
  };

  const addTarget = async () => {
    const cleanUrl = url.trim();
    if (!cleanUrl) return;
    setSaving(true);
    // Store platform lowercase so it stays consistent with the scrapers, which
    // query .eq('platform','reddit'/'youtube'). The dropdown shows nice-cased
    // labels (Reddit, YouTube) but the stored value is always normalized.
    const { data, error } = await supabase
      .from('hunter_targets')
      .insert({ platform: platform.toLowerCase(), url: cleanUrl, keyword_categories: cats })
      .select()
      .single();
    if (!error && data) {
      setTargets((prev) => [data, ...prev]);
      resetForm();
    }
    setSaving(false);
  };

  const toggleStatus = async (t) => {
    setBusyId(t.id);
    const next = t.status === 'active' ? 'paused' : 'active';
    const { error } = await supabase.from('hunter_targets').update({ status: next }).eq('id', t.id);
    if (!error) setTargets((prev) => prev.map((x) => (x.id === t.id ? { ...x, status: next } : x)));
    setBusyId(null);
  };

  const deleteTarget = async (id) => {
    setBusyId(id);
    const { error } = await supabase.from('hunter_targets').delete().eq('id', id);
    if (!error) setTargets((prev) => prev.filter((x) => x.id !== id));
    setBusyId(null);
  };

  // Platform values are stored inconsistently (seeds lowercase 'reddit'/'youtube',
  // manual adds capitalized 'Reddit'); match case-insensitively everywhere.
  const ofPlatform = useCallback(
    (key) => targets.filter((t) => (t.platform || '').toLowerCase() === key),
    [targets],
  );

  // Bulk toggle: if ANY of the platform's targets are active, pause them all;
  // if all are already paused, resume them all. Driven by computed IDs so it
  // only touches what's loaded and sidesteps the platform-casing mismatch.
  const bulkToggle = async (key) => {
    const rows = ofPlatform(key);
    if (!rows.length) return;
    const anyActive = rows.some((t) => t.status === 'active');
    const next = anyActive ? 'paused' : 'active';
    const ids = rows.map((t) => t.id);
    setBulkBusy(key);
    const { error } = await supabase.from('hunter_targets').update({ status: next }).in('id', ids);
    if (!error) {
      const idSet = new Set(ids);
      setTargets((prev) => prev.map((x) => (idSet.has(x.id) ? { ...x, status: next } : x)));
    }
    setBulkBusy(null);
  };

  const redditRows = ofPlatform('reddit');
  const youtubeRows = ofPlatform('youtube');
  const redditAnyActive = redditRows.some((t) => t.status === 'active');
  const youtubeAnyActive = youtubeRows.some((t) => t.status === 'active');

  const visibleTargets = useMemo(() => {
    if (platformFilter === 'all') return targets;
    return targets.filter((t) => (t.platform || '').toLowerCase() === platformFilter);
  }, [targets, platformFilter]);

  return (
    <div>
      {/* Toolbar: platform filter + bulk controls + add */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Platform filter */}
          <div className="flex items-center gap-1">
            {PLATFORM_FILTERS.map((f) => {
              const isActive = platformFilter === f.key;
              const count =
                f.key === 'all' ? targets.length : ofPlatform(f.key).length;
              return (
                <button
                  key={f.key}
                  onClick={() => setPlatformFilter(f.key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-gray-900 text-white'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 text-xs ${isActive ? 'text-gray-300' : 'text-gray-400'}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Bulk pause/resume */}
          <div className="flex items-center gap-2">
            <button
              className={btnGhost}
              disabled={bulkBusy === 'reddit' || redditRows.length === 0}
              onClick={() => bulkToggle('reddit')}
            >
              {bulkBusy === 'reddit' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : redditAnyActive ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {redditAnyActive ? 'Pause All Reddit' : 'Resume All Reddit'}
            </button>
            <button
              className={btnGhost}
              disabled={bulkBusy === 'youtube' || youtubeRows.length === 0}
              onClick={() => bulkToggle('youtube')}
            >
              {bulkBusy === 'youtube' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : youtubeAnyActive ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {youtubeAnyActive ? 'Pause All YouTube' : 'Resume All YouTube'}
            </button>
          </div>
        </div>

        <button className={showAdd ? btnGhost : btnPrimary} onClick={() => setShowAdd((v) => !v)}>
          {showAdd ? <ChevronDown className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          {showAdd ? 'Cancel' : 'Add target'}
        </button>
      </div>

      {showAdd && (
        <div className={`${card} p-5 mb-4`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Platform</label>
              <select className={`${input} w-full`} value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {PLATFORMS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">URL or group identifier</label>
              <input
                className={`${input} w-full`}
                placeholder="https://reddit.com/r/… or subreddit name"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTarget()}
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Keyword categories to apply</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const on = cats.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => toggleCat(c)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                      on ? categoryPill[c] : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {c}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Leave all unselected to apply every active keyword.</p>
          </div>
          <button className={btnPrimary} disabled={saving || !url.trim()} onClick={addTarget}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Add target
          </button>
        </div>
      )}

      {/* Table */}
      <div className={`${card} shadow-sm overflow-hidden`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              <th className="px-4 py-3 w-32">Platform</th>
              <th className="px-4 py-3">URL / Group</th>
              <th className="px-4 py-3 w-56">Categories</th>
              <th className="px-4 py-3 w-28">Status</th>
              <th className="px-4 py-3 w-32">Last scanned</th>
              <th className="px-4 py-3 w-24 text-right">Signals</th>
              <th className="px-4 py-3 w-40 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td>
              </tr>
            ) : visibleTargets.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                  {targets.length === 0
                    ? 'No targets yet. Add a source to start monitoring.'
                    : `No ${platformFilter} targets.`}
                </td>
              </tr>
            ) : (
              visibleTargets.map((t) => {
                const busy = busyId === t.id;
                const isActive = t.status === 'active';
                return (
                  <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700 capitalize">
                        {t.platform}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <a
                        href={targetHref(t)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 truncate"
                      >
                        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{t.url}</span>
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(t.keyword_categories || []).length === 0 ? (
                          <span className="text-xs text-gray-400">All</span>
                        ) : (
                          (t.keyword_categories || []).map((c) => (
                            <span key={c} className={`px-1.5 py-0.5 text-[11px] font-medium rounded-full ${categoryPill[c] || ''}`}>
                              {c}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full capitalize ${statusPill[t.status] || ''}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{relativeTime(t.last_scanned_at)}</td>
                    <td className="px-4 py-3 text-right text-gray-700 tabular-nums">{t.signals_found ?? 0}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {/* Clear, labeled pause/resume toggle */}
                        <button
                          onClick={() => toggleStatus(t)}
                          disabled={busy}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border transition-colors disabled:opacity-50 ${
                            isActive
                              ? 'border-amber-200 text-amber-700 bg-amber-50 hover:bg-amber-100'
                              : 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100'
                          }`}
                        >
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : isActive ? (
                            <Pause className="w-3.5 h-3.5" />
                          ) : (
                            <Play className="w-3.5 h-3.5" />
                          )}
                          {isActive ? 'Pause' : 'Resume'}
                        </button>
                        <button
                          title="Delete"
                          onClick={() => deleteTarget(t.id)}
                          disabled={busy}
                          className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-rose-500 transition-colors disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
